import { Component, Input, inject } from '@angular/core';
import {
  BlockType,
  Booking,
  BookingStatus,
} from '../../../core/models/entities/booking';
import { Room } from '../../../core/models/entities/room';
import { differenceInHours, format, parseISO } from 'date-fns';
import { TranslocoService } from '@jsverse/transloco';
import { RoomColor } from '../../../core/helpers/room-color.helper';
import { BookingTooltipHelper } from '../../../core/helpers/booking-tooltip.helper';

@Component({
  selector: 'app-booking-calendar-block',
  template: `
    @if (compact) {
      <div
        class="relative flex h-6 items-center gap-1.5 overflow-visible border-l-[3px] px-1.5 text-xs transition-colors"
        data-cy="booking"
        [attr.data-status]="booking.status"
        [ngClass]="blockClasses"
        [tooltip]="room.active ? tooltip : ''"
        [style.cursor]="
          isBookingOrOption && room.active ? 'pointer' : 'default'
        "
      >
        @if (isBookingOrOption) {
          <app-booking-characteristic-icons class="text-[10px]" [booking]="booking" />
        }
        <span class="shrink-0 font-semibold tabular-nums">{{
          startTimeText
        }}</span>
        <span data-cy="booking-name" class="truncate">
          @if (isBookingOrOption) {
            {{ name }}
          } @else if (isBlocked) {
            {{ blockedLabel }}
          }
        </span>
        @if (
          booking.status === BookingStatus.Pending &&
          booking.block_type === BlockType.Booking
        ) {
          <span
            class="absolute -top-1 -right-1 size-2.5 rounded-full bg-pink-600 ring-2 ring-white"
          ></span>
        }
      </div>
    } @else {
      <div
        class="relative flex h-full flex-col justify-center overflow-visible border-l-[3px] px-2 transition-colors"
        data-cy="booking"
        [attr.data-status]="booking.status"
        [ngClass]="blockClasses"
        [tooltip]="room.active ? tooltip : ''"
        [style.cursor]="
          isBookingOrOption && room.active ? 'pointer' : 'default'
        "
      >
        <div
          data-cy="booking-name"
          class="flex items-center gap-1.5 truncate text-[13px] font-medium"
        >
          @if (isBookingOrOption) {
            <app-booking-characteristic-icons class="text-xs" [booking]="booking" />
          }
          <span class="truncate">
            @if (isBookingOrOption) {
              {{ name }}
            } @else if (isBlocked) {
              {{ blockedLabel }}
            }
          </span>
        </div>
        <div
          data-cy="booking-start-end"
          class="truncate text-xs tabular-nums opacity-75"
        >
          {{ startEndText }}
          @if (endsNextDay) {
            <span class="pl-1">+1</span>
          }
        </div>
        @if (
          booking.status === BookingStatus.Pending &&
          booking.block_type === BlockType.Booking
        ) {
          <span
            class="absolute -top-1 -right-1 size-2.5 rounded-full bg-pink-600 ring-2 ring-white"
          ></span>
        }
      </div>
    }
  `,
  standalone: false,
})
export class BookingCalendarBlockComponent {
  private translocoService = inject(TranslocoService);

  @Input() room: Room;
  @Input() booking: Booking;
  @Input() name: string;
  @Input({ required: true }) color: RoomColor;
  @Input({ required: true }) start: string;
  @Input({ required: true }) end: string;
  @Input({ required: true }) endsNextDay: boolean;
  @Input({ required: true }) overflowsEnd: boolean;
  @Input({ required: true }) overflowsStart: boolean;
  @Input() compact = false;

  // Appelé depuis le template : évalué après le chargement des traductions.
  protected get blockedLabel(): string {
    return this.translocoService.translate('bookings.calendar-block.blocked');
  }

  get startEndText() {
    const start = parseISO(this.start);
    const end = parseISO(this.end);
    return differenceInHours(end, start) <= 24
      ? format(start, 'HH:mm') + ' - ' + format(end, 'kk:mm')
      : format(
          start,
          this.translocoService.translate(
            'common.date-format.datetime-numeric',
          ),
        ) +
          ' - ' +
          format(
            end,
            this.translocoService.translate(
              'common.date-format.datetime-numeric-short',
            ),
          );
  }

  get startTimeText(): string {
    return format(parseISO(this.start), 'HH:mm');
  }

  get tooltip(): string {
    if (this.isBlocked) {
      return BookingTooltipHelper.tooltipOf(
        this.booking,
        this.room,
        (key) => this.translocoService.translate(key),
        this.blockedLabel,
      );
    }
    if (!this.isBookingOrOption) {
      return '';
    }
    return BookingTooltipHelper.tooltipOf(this.booking, this.room, (key) =>
      this.translocoService.translate(key),
    );
  }

  get blockClasses(): string[] {
    return [
      this.colorClasses,
      this.isPending ? 'opacity-60' : '',
      this.overflowsStart ? 'rounded-l-none' : 'rounded-l-md',
      this.overflowsEnd ? 'rounded-r-none' : 'rounded-r-md',
      this.isBookingOrOption ? 'z-10' : 'z-0',
    ];
  }

  private get colorClasses(): string {
    if (this.isBlocked) {
      return 'border-gray-300 text-gray-500 bg-[repeating-linear-gradient(135deg,var(--color-gray-100)_0_6px,var(--color-gray-200)_6px_12px)]';
    }
    if (!this.room.active) {
      return 'border-gray-300 bg-gray-100 text-gray-500';
    }
    const optionOpacity =
      this.booking.block_type === BlockType.Option ? 'opacity-50' : '';
    return `${this.color.pill} ${this.color.accent} ${optionOpacity}`;
  }

  private get isPending(): boolean {
    return (
      this.booking.status === BookingStatus.Pending ||
      this.booking.status === BookingStatus.PendingPayment
    );
  }

  get isBookingOrOption() {
    return (
      this.booking.block_type === BlockType.Booking ||
      this.booking.block_type === BlockType.Option
    );
  }

  get isBlocked() {
    return (
      this.booking.block_type === BlockType.RoomBlocked ||
      this.booking.block_type === BlockType.RoomWithoutControl
    );
  }

  protected readonly BookingStatus = BookingStatus;
  protected readonly BlockType = BlockType;
}
